import { useMemo } from 'react'
import { type TreeNode } from 'domains/retailer-collections/components/browse/utils/types'
import { useRetailerCollectionsTree } from 'domains/retailer-collections/components/browse/utils/useRetailerCollectionsTree.hooks'
import { RetailerCollectionContentType } from 'domains/retailer-collections/utils/types'
import { MARKETPLACE_STORE_CONFIG_ID } from 'domains/retailer-collections/utils/constants'

export type RetailerCollectionOption = { label: string; value: string; level: number }

const onlyCollectionsContentType = (v: TreeNode) =>
  v.contentType === RetailerCollectionContentType.Collections

const extractOptions = (node: TreeNode, prefix = '', level = 0): RetailerCollectionOption[] => {
  const label = [prefix, node.label].filter(Boolean).join(' > ')
  const result = [
    {
      label,
      value: node.key,
      level,
    },
  ]

  const childOptions = node.nodes
    ?.filter(onlyCollectionsContentType)
    .map(v => extractOptions(v, label, level + 1))
    .flat()

  if (childOptions) return result.concat(childOptions)

  return result
}

export const useGetRetailerCollectionsOptions = ({
  maxLevel = Infinity,
}: { maxLevel?: number } = {}) => {
  // Note: this does not use the browse context because it can be shown outside of it.
  const { tree, loading, error } = useRetailerCollectionsTree({
    storeConfigurationId: '0',
  })

  const options = useMemo<RetailerCollectionOption[]>(
    () =>
      [
        {
          label: 'No parent collection',
          value: undefined,
          level: 0,
        },
      ]
        .concat(tree?.filter(onlyCollectionsContentType).flatMap(node => extractOptions(node)))
        .filter(Boolean)
        .filter(option => option.level < maxLevel),
    [tree, maxLevel]
  )

  return {
    options,
    loading,
    error,
  }
}
